<template>
  <v-main>

    <v-container
        id="suspend-member-container"
        fluid
        tag="section"
    >

      <base-material-card
          icon="mdi-account-cancel-outline"
          class="px-5 py-3"
          ref="form"
      >

        <shared-page-messages :error-messages="errorMessages"
                              :warning-messages="warningMessages"
                              :success-messages="successMessages">
        </shared-page-messages>

        <v-form ref="suspendMemberForm"
                v-model="valid"
                @submit.prevent="validate"
                lazy-validation
        >

          <v-row>
            <v-col
                cols="12"
                sm="6"
            >
              <v-combobox
                  v-model="form.user"
                  label="Member"
                  placeholder="Start typing to search"
                  :items="computedUserList"
                  :loading="isLoadingUsers"
                  :search-input.sync="userSearchString"
                  @input.native="nativeUserSearchString=$event.srcElement.value"
                  item-text="names"
                  item-value="id"
                  return-object
                  persistent-placeholder
                  no-filter
                  validate-on-blur
                  chips
                  @change="fetchUserInvoices"
                  :rules="rules.validateMembers"
              ></v-combobox>

            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="9"
            >
              <v-select
                  v-model="form.invoice"
                  label="Invoice"
                  :items="computedInvoiceList"
                  item-text="description"
                  item-value="id"
                  return-object
                  :rules="rules.required"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="3"
            >
              <v-text-field
                  v-model="form.startDate"
                  label="Start Date"
                  type="date"
                  :rules="rules.date"
              ></v-text-field>
            </v-col>

            <v-col
                cols="12"
                sm="3"
            >
              <v-text-field
                  v-model="form.period"
                  label="Period (in days)"
                  type="number"
                  :rules="rules.period"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="9"
            >
              <v-text-field
                  v-model="form.comment"
                  label="Comment"
                  type="text"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="6"
                sm="4"
            >
              <div class="my-2">
                <v-btn
                    :disabled="processing"
                    :loading="processing"
                    color="primary"
                    dark
                    type="submit"
                >
                  Submit
                </v-btn>
              </div>
            </v-col>
          </v-row>

        </v-form>


      </base-material-card>

      <v-expansion-panels
          multiple
          light
          hover
          v-model="expandedPanels"
          v-if="suspensionId"
      >
        <v-expansion-panel>
          <v-expansion-panel-header>Audit Trail</v-expansion-panel-header>

          <v-expansion-panel-content>
            <shared-audit-trail-view :item-id="suspensionId"></shared-audit-trail-view>
          </v-expansion-panel-content>

        </v-expansion-panel>
      </v-expansion-panels>


    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import validation from "@/utils/validation"
import moment from "moment"
import SharedPageMessages from '../shared/PageMessages.vue'
import SharedAuditTrailView from '../shared/AuditTrailView.vue'

export default {
  name: 'CoreSuspendMemberView',
  components: {
    SharedAuditTrailView,
    SharedPageMessages
  },

  async created() {
    this.userId = this.$route.params.memberId
    this.suspensionId = this.$route.params.suspensionId

    if (this.userId !== undefined && this.suspensionId !== undefined) {
      await this.fetchSuspension()
      if (this.form.user.id !== undefined) {
        await this.fetchUserInvoices(this.form.user)
      }
    }
  },

  computed: {
    computedUserList: {
      get() {
        return this.users
            .map(u => ({ id: u.id, names: u.names}))
            .sort((a, b) => a.names.localeCompare(b.names))
      }
    },
    computedInvoiceList: {
      get() {
        return this.invoices
            .map(inv => {
              let description = `${inv.id} - ${inv.items[0].description} (${inv.status})`
              return {
                id: inv.id,
                description: description,
                invoiceDate: inv.invoiceDate,
              }
            })
            .sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate))
      }
    },
    computedStartDate: {
      get() {
        if (this.form.startDate == null) {
          return
        }
        return moment(this.form.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      },
      set(value) {
        this.form.startDate = value
      }
    },

  },

  watch: {
    errorMessages: function () {
      scroll(0, 0)
    },
    successMessages: function () {
      scroll(0, 0)
    },
    warningMessages: function () {
      scroll(0, 0)
    },
    userSearchString(val) {
      if (!this.isLoadingUsers && val !== null && (val.length > 2)) {
        this.fetchUsers(val)
      }
    }
  },

  methods: {
    clearErrors() {
      this.errorMessages = []
      this.warningMessages = []
      this.successMessages = []
    },
    validate() {
      if(this.$refs.suspendMemberForm.validate()) {
        if (this.form.suspensionId === null) {
          this.createSuspension()
        } else {
          this.updateSuspension()
        }
      }
    },

    buildPayload() {
      return {
        invoiceId: this.form.invoice.id,
        startDate: this.form.startDate,
        period: this.form.period,
        comment: this.form.comment
      }
    },

    buildForm(suspension) {
      let user = {id: suspension.user.id, names: suspension.user.names}
      this.form.suspensionId = suspension.id
      this.form.startDate = suspension.startDate
      this.form.endDate = suspension.endDate
      this.form.period = suspension.period
      this.form.invoice = suspension.invoice
      this.form.user = user
      this.form.comment = suspension.comment
      this.users.push(user)
    },

    async fetchUsers(val) {
      this.clearErrors()
      this.isLoadingUsers = true
      let nameParts = val.split(' ')
      let firstPart = nameParts.shift()
      let lastPart = nameParts.pop() || firstPart
      let filters = {
        firstName: firstPart,
        lastName: lastPart,
        searchCondition: 'OR'
      }
      return api.fetchUsers(filters)
          .then(response => {
            this.users = response.data.content
          })
          .catch(err => {
            this.handleError(err)
          })
          .finally(this.isLoadingUsers = false)
    },
    async fetchUserInvoices(user) {
      this.clearErrors()
      this.isLoadingInvoices = true
      let filterParams = {
        userId: user.id
      }
      return api.fetchInvoices(filterParams)
          .then(response => {
            this.invoices = response.data.content
          })
          .catch(err => {
            this.handleError(err)
          })
          .finally(this.isLoadingInvoices = false)
    },
    async fetchSuspension() {
      this.clearErrors()
      this.isLoadingSuspension = true
      return api.fetchUserSuspension(this.userId, this.suspensionId)
          .then(response => {
            let suspension = response.data
            if (suspension) {
              this.buildForm(suspension)
            }
          })
          .catch(err => this.handleError(err))
          .finally(this.isLoadingSuspension = false)
    },
    async createSuspension() {
      this.processing = true
      this.clearErrors()
      let payload = this.buildPayload()
      let userId = this.form.user.id
      return api.createUserSuspension(userId, payload)
          .then(() => {
            this.$router.push({ path: '/suspensions/view' })
          })
          .catch(err => this.handleError(err))
          .finally(this.processing = false)
    },
    async updateSuspension() {
      this.processing = true
      this.clearErrors()
      let payload = this.buildPayload()
      let userId = this.form.user.id
      let suspensionId = this.form.suspensionId
      return api.updateUserSuspension(userId, suspensionId, payload)
          .then(() => {
              this.$router.push({ path: '/suspensions/view' })
          })
          .catch(err => this.handleError(err))
          .finally(this.processing = false)
    },
    handleError(err) {
      let errorMessage = api.parseResponseError(err, true)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      valid: true,
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      form: {
        suspensionId: null,
        user: null,
        invoice: null,
        period: 15,
        startDate: moment().format('YYYY-MM-DD'),
        comment: null,
      },
      userId: null,
      suspensionId: null,
      users: [],
      invoices: [],
      selectedUsers: [],
      userSearchString: '',
      nativeUserSearchString: '',
      processing: false,
      isLoadingUsers: false,
      isLoadingInvoices: false,
      isLoadingSuspension: false,
      expandedPanels: [0],
      rules: {
        required: [
          v => validation.isRequired(v) || 'required'
        ],
        period: [
          v => validation.isRequired(v) || 'required',
          v => validation.isMoreThan(v,0) || 'must be be more that 0 days'
        ],
        date: [
          v => validation.isDate(v) || 'must be a valid date in the format mm/dd/yyyy'
        ],
        validateMembers: [
          v => validation.isRequired(v) || 'required',
          v => validation.isContainedInObjectsList(v, this.computedUserList) || 'must be in the list of members'
        ]
      }
    }
  }
}
</script>


<style lang="sass" scoped>
.invoice-all-members-checkbox-label
  color: #333
</style>
